import React, { useEffect, useMemo, useState } from 'react';
import {
  Inbox, Workflow, Radar, ShieldCheck, Mail, MailSearch, Brain, Handshake, UserCheck, Send, Trophy, Bot, Hourglass,
  CheckCircle2, Loader2, Circle, MinusCircle, XCircle, AlertCircle, StopCircle, X
} from 'lucide-react';
import { api, Button, Notice, timeAgo, formatWhen, tierLabel } from './ui';

/** The agent team as a live network: every agent a card with its steps, linked by the work passing between them. */

// Fixed canvas geometry (px), no DOM measuring. Narrow screens scroll the canvas sideways.
const TOP = 40;
const HEAD = 44;
const ROW = 18;
const PAD = 10;
const MID_MIN = 64;
const MID_HEAD = 190;
const W = 1208;
const LOOP = 34;
const COLS = {
  left: { x: 16, w: 232, gap: 16, i: 0 },
  mid: { x: 300, w: 600, gap: 10, i: 1 },
  right: { x: 952, w: 240, gap: 16, i: 2 }
};
// Dotted grid; the dot colour comes from a CSS variable so dark mode can swap it.
const GRID = { backgroundImage: 'radial-gradient(var(--dot, #dadce0) 1px, transparent 1px)', backgroundSize: '16px 16px' };

const list = (v) => (Array.isArray(v) ? v : []);

const ICONS = {
  inbox: Inbox, orchestrator: Workflow, scout: Radar, vetter: ShieldCheck, outreach: Mail,
  classifier: MailSearch, strategist: Brain, negotiator: Handshake, approvals: UserCheck, sender: Send, deals: Trophy,
  stopper: Hourglass
};

const STEP = {
  done: { Icon: CheckCircle2, cls: 'text-green-600 dark:text-green-500', text: 'text-gray-800 dark:text-slate-200', word: 'Done' },
  run: { Icon: Loader2, cls: 'animate-spin text-blue-600 dark:text-blue-400', text: 'text-gray-800 dark:text-slate-200', word: 'Running' },
  wait: { Icon: Circle, cls: 'text-gray-400 dark:text-slate-500', text: 'text-gray-600 dark:text-slate-400', word: 'Waiting' },
  skip: { Icon: MinusCircle, cls: 'text-gray-400 dark:text-slate-500', text: 'text-gray-400 dark:text-slate-500', word: 'Skipped' },
  fail: { Icon: XCircle, cls: 'text-red-600 dark:text-red-400', text: 'text-gray-800 dark:text-slate-200', word: 'Failed' }
};

const MARK = {
  run: { Icon: Loader2, cls: 'animate-spin text-blue-600 dark:text-blue-400' },
  done: { Icon: CheckCircle2, cls: 'text-green-600 dark:text-green-500' },
  fail: { Icon: XCircle, cls: 'text-red-600 dark:text-red-400' },
  attn: { Icon: AlertCircle, cls: 'text-amber-500 dark:text-amber-400' },
  off: { Icon: StopCircle, cls: 'text-gray-400 dark:text-slate-500' }
};

const CARD = {
  wait: 'border-gray-300 bg-white dark:border-slate-700 dark:bg-slate-900',
  run: 'border-blue-500 bg-white dark:border-blue-400 dark:bg-slate-900',
  done: 'border-green-600 bg-white dark:border-green-500 dark:bg-slate-900',
  fail: 'border-red-500 bg-white dark:border-red-400 dark:bg-slate-900',
  attn: 'border-amber-500 bg-white dark:border-amber-400 dark:bg-slate-900',
  off: 'border-dashed border-gray-300 bg-gray-50 dark:border-slate-700 dark:bg-slate-800/60'
};

// Rank sets the drawing order: active links go last so they sit on top where links cross.
const EDGE = {
  idle: { cls: 'stroke-gray-300 dark:stroke-slate-600', w: 1.5, rank: 0, label: 'Idle' },
  off: { cls: 'stroke-gray-300 dark:stroke-slate-600', w: 1.5, dash: '3 4', rank: 0, label: 'Off' },
  used: { cls: 'stroke-gray-400 dark:stroke-slate-400', w: 1.5, rank: 1, label: 'Used' },
  active: { cls: 'stroke-blue-600 dark:stroke-blue-400', w: 2, rank: 2, label: 'Active' }
};

const RUN_BADGE = {
  running: { label: 'Running', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
  done: { label: 'Done', cls: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' }
};

// Service nodes keep no run log; their panel shows items and details instead.
const NO_RUNS = new Set(['inbox', 'sender', 'approvals', 'deals']);

/* ---- geometry ---- */

const colOf = (n) => (COLS[n.column] ? n.column : 'mid');

const rowsOf = (n, mid) => {
  if (Array.isArray(n.items)) return Math.min(n.items.length, 3) * 2 || 1;
  const k = list(n.steps).length;
  return mid ? Math.ceil(k / 3) : k;
};

// Middle rows put the title block beside the steps, so only the step grid sets their height.
const heightOf = (n, mid) => {
  const rows = rowsOf(n, mid);
  return mid ? Math.max(MID_MIN, rows * (ROW + 2) + 18) : HEAD + rows * ROW + PAD;
};

const layout = (nodes) => {
  const stacks = { left: [], mid: [], right: [] };
  nodes.forEach((n) => stacks[colOf(n)].push(n));
  Object.values(stacks).forEach((s) => s.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));
  const total = (key) => stacks[key].reduce((s, n, i) => s + heightOf(n, key === 'mid') + (i ? COLS[key].gap : 0), 0);
  const tallest = Math.max(total('left'), total('mid'), total('right'));
  const box = {};
  Object.keys(stacks).forEach((key) => {
    let y = TOP + Math.round((tallest - total(key)) / 2);
    stacks[key].forEach((n) => {
      const h = heightOf(n, key === 'mid');
      box[n.id] = { x: COLS[key].x, y, w: COLS[key].w, h, col: key };
      y += h + COLS[key].gap;
    });
  });
  const order = {};
  Object.keys(stacks).forEach((key) => { order[key] = stacks[key].map((n) => n.id); });
  return { box, order, height: TOP + tallest + 24 };
};

/** Across columns side to side, between neighbours bottom to top, otherwise round the right of the column. */
const route = (from, to, L) => {
  const a = L.box[from];
  const b = L.box[to];
  if (!a || !b) return null;
  const ca = COLS[a.col].i;
  const cb = COLS[b.col].i;
  if (ca !== cb) {
    const x0 = ca < cb ? a.x + a.w : a.x;
    const x3 = ca < cb ? b.x : b.x + b.w;
    const y0 = a.y + a.h / 2;
    const y3 = b.y + b.h / 2;
    const mx = (x0 + x3) / 2;
    return { d: `M${x0},${y0} C${mx},${y0} ${mx},${y3} ${x3},${y3}`, reach: Math.max(x0, x3) };
  }
  const stack = L.order[a.col];
  const gap = stack.indexOf(to) - stack.indexOf(from);
  if (Math.abs(gap) === 1) {
    const x = a.x + a.w / 2;
    const y0 = gap > 0 ? a.y + a.h : a.y;
    const y3 = gap > 0 ? b.y : b.y + b.h;
    return { d: `M${x},${y0} L${x},${y3}`, reach: x };
  }
  const x0 = a.x + a.w;
  const x3 = b.x + b.w;
  const y0 = a.y + a.h / 2;
  const y3 = b.y + b.h / 2;
  const lane = Math.max(x0, x3) + LOOP;
  return { d: `M${x0},${y0} C${lane},${y0} ${lane},${y3} ${x3},${y3}`, reach: lane };
};

const duration = (start, end) => {
  if (!start) return null;
  const raw = ((end ? new Date(end) : new Date()).getTime() - new Date(start).getTime()) / 1000;
  if (Number.isNaN(raw)) return null;
  const s = Math.max(0, Math.round(raw));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

/* ---- cards ---- */

export const StepIcon = ({ state, size = 'h-3.5 w-3.5', still }) => {
  const s = STEP[state] || STEP.wait;
  return <s.Icon className={`${size} flex-none ${still ? s.cls.replace('animate-spin ', '') : s.cls}`} aria-hidden="true" />;
};

const Steps = ({ steps, mid }) => (
  <ul className={mid ? 'grid grid-cols-3 gap-x-2 gap-y-0.5 pr-2.5' : 'px-2.5'}>
    {steps.map((s, i) => {
      const st = STEP[s.state] || STEP.wait;
      const label = s.label || s.key;
      return (
        <li key={s.key || i} title={`${label}: ${s.note || st.word}`} className={`flex h-[18px] min-w-0 items-center gap-1 text-xs leading-4 ${st.text}`}>
          <StepIcon state={s.state} />
          <span className="truncate">{label}</span>
        </li>
      );
    })}
  </ul>
);

const Items = ({ items, mid }) => (
  <div className={mid ? 'min-w-0 pr-2.5' : 'px-2.5'}>
    {!items.length && <div className="h-[18px] text-xs leading-[18px] text-gray-400 dark:text-slate-500">Nothing here yet</div>}
    {items.slice(0, 3).map((it, i) => (
      <div key={i} className="min-w-0">
        <div className="h-[18px] truncate text-xs leading-[18px] text-gray-800 dark:text-slate-200" title={it.text || ''}>{it.text}</div>
        <div className="h-[18px] truncate text-[11px] leading-[18px] text-gray-500 dark:text-slate-400" title={it.sub || ''}>{it.sub}</div>
      </div>
    ))}
  </div>
);

// The tier, never the model id: see tierLabel in ui.jsx. Nothing is shown for
// a model no agent is on any more.
const ModelChip = ({ model }) => {
  const label = tierLabel(model);
  if (!label) return null;
  return (
    <span className="max-w-[96px] flex-none truncate rounded-full bg-gray-100 px-1.5 text-[10px] leading-4 text-gray-600 dark:bg-slate-800 dark:text-slate-300" title={`Runs on the ${label.toLowerCase()}`}>{label}</span>
  );
};

const Head = ({ node, mid }) => {
  const Icon = ICONS[node.icon] || Bot;
  const M = MARK[node.state];
  return (
    <div className={`flex min-w-0 items-center gap-2 ${mid ? 'pl-2.5 pr-2' : 'h-11 px-2.5'}`}>
      <Icon className="h-[18px] w-[18px] flex-none text-gray-600 dark:text-slate-300" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1.5">
          <span className="truncate text-[13px] font-semibold leading-4 text-gray-900 dark:text-slate-100" title={node.label}>{node.label}</span>
          {node.queue > 0 && <span className="flex-none rounded-full bg-blue-600 px-1.5 text-[10px] font-medium leading-4 text-white" title={`${node.queue} waiting`}>{node.queue}</span>}
          {!mid && node.model && <ModelChip model={node.model} />}
        </div>
        <div className="truncate text-xs leading-4 text-gray-500 dark:text-slate-400" title={node.sub || ''}>{node.sub || ' '}</div>
        {mid && node.model && <div className="mt-0.5 flex"><ModelChip model={node.model} /></div>}
      </div>
      {M && <M.Icon className={`h-4 w-4 flex-none ${M.cls}`} aria-label={node.state} />}
    </div>
  );
};

const NodeCard = ({ node, box, selected, onSelect }) => {
  const mid = box.col === 'mid';
  const items = Array.isArray(node.items) ? node.items : null;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); } }}
      className={`absolute cursor-pointer overflow-hidden rounded-lg border-[1.5px] text-left transition-colors hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 ${CARD[node.state] || CARD.wait} ${selected ? 'shadow-md ring-2 ring-blue-200 dark:ring-blue-900' : ''} ${mid ? 'grid items-center' : ''}`}
      style={{ left: box.x, top: box.y, width: box.w, height: box.h, gridTemplateColumns: mid ? `${MID_HEAD}px minmax(0, 1fr)` : undefined }}
    >
      <Head node={node} mid={mid} />
      {items ? <Items items={items} mid={mid} /> : <Steps steps={list(node.steps)} mid={mid} />}
    </div>
  );
};

const Legend = () => (
  <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs text-gray-500 dark:text-slate-400">
    {['done', 'run', 'wait', 'skip', 'fail'].map((k) => (
      <span key={k} className="inline-flex items-center gap-1.5"><StepIcon state={k} size="h-4 w-4" still />{STEP[k].word}</span>
    ))}
    {['active', 'used', 'off'].map((k) => (
      <span key={k} className="inline-flex items-center gap-1.5">
        <svg width="22" height="6" aria-hidden="true">
          <line x1="1" y1="3" x2="21" y2="3" className={EDGE[k].cls} strokeWidth={EDGE[k].w} strokeDasharray={EDGE[k].dash} />
        </svg>
        {EDGE[k].label}
      </span>
    ))}
    <span>Click an agent to see its recent runs.</span>
  </div>
);

/* ---- side panel ---- */

const RunCard = ({ run, onOpenOpportunity }) => {
  const badge = RUN_BADGE[run.status] || { label: run.status || '—', cls: 'bg-gray-100 text-gray-700 dark:bg-slate-800 dark:text-slate-300' };
  const dur = duration(run.started_at, run.status === 'running' ? null : run.finished_at);
  const steps = list(run.steps);
  return (
    <li className="rounded-lg border border-gray-200 p-3 dark:border-slate-700">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 dark:text-slate-100">{run.title || 'Run'}</div>
          {run.domain && (run.opportunity_id && onOpenOpportunity
            ? <button type="button" onClick={() => onOpenOpportunity(run.opportunity_id)} className="text-xs text-blue-600 hover:underline dark:text-blue-400">{run.domain}</button>
            : <div className="text-xs text-gray-600 dark:text-slate-400">{run.domain}</div>)}
        </div>
        <span className={`flex-none whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${badge.cls}`}>{badge.label}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-1.5 text-xs text-gray-500 dark:text-slate-400">
        <span title={formatWhen(run.started_at)}>{timeAgo(run.started_at)}</span>
        {dur && <span>· {dur}</span>}
        {tierLabel(run.model) && <span>· {tierLabel(run.model)}</span>}
      </div>
      {run.summary && <p className="mt-1.5 whitespace-pre-wrap text-xs text-gray-700 dark:text-slate-300">{run.summary}</p>}
      {steps.length > 0 && (
        <ul className="mt-2 space-y-1">
          {steps.map((s, i) => (
            <li key={s.key || i} className="flex gap-1.5 text-xs">
              <span className="mt-px"><StepIcon state={s.state} /></span>
              <span className="min-w-0">
                <span className={(STEP[s.state] || STEP.wait).text}>{s.label || s.key}</span>
                {s.note && <span className="text-gray-500 dark:text-slate-400"> · {s.note}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const Panel = ({ teamId, node, onClose, onOpenOpportunity, onApproach }) => {
  const Icon = ICONS[node.icon] || Bot;
  const wantRuns = !Array.isArray(node.items) && !NO_RUNS.has(node.id);
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  // Reload the run log whenever the card's own progress moves.
  const progress = `${node.last_run_at || ''}|${node.state}|${list(node.steps).map((s) => s.state).join(',')}`;

  useEffect(() => {
    if (!wantRuns) return undefined;
    let alive = true;
    api(`/teams/${teamId}/runs?agent=${encodeURIComponent(node.id)}&limit=20`)
      .then((d) => { if (alive) { setRuns(list(d.runs)); setError(null); } })
      .catch((err) => { if (alive) setError(err.message); });
    return () => { alive = false; };
  }, [teamId, node.id, wantRuns, progress]);

  const items = list(node.items);
  const details = list(node.details);
  return (
    <aside className="w-full flex-none self-start rounded-xl border border-gray-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900 xl:sticky xl:top-4 xl:max-h-[calc(100vh-6rem)] xl:w-[380px] xl:overflow-y-auto">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 flex-none text-gray-600 dark:text-slate-300" />
        <h3 className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900 dark:text-slate-100">{node.label}</h3>
        {node.model && <ModelChip model={node.model} />}
        <button type="button" onClick={onClose} title="Close" className="text-gray-400 hover:text-gray-700 dark:hover:text-slate-200"><X className="h-5 w-5" /></button>
      </div>
      {node.sub && <p className="mt-1 text-xs text-gray-500 dark:text-slate-400">{node.sub}</p>}
      {node.last_run_at && <p className="mt-0.5 text-xs text-gray-400 dark:text-slate-500" title={formatWhen(node.last_run_at)}>Last ran {timeAgo(node.last_run_at)}</p>}

      {node.id === 'outreach' && onApproach && (
        <div className="mt-3"><Button variant="primary" icon={Mail} onClick={() => onApproach()}>Approach an affiliate</Button></div>
      )}

      {details.length > 0 && (
        <dl className="mt-3 space-y-1 rounded-lg bg-gray-50 p-3 text-xs dark:bg-slate-800/60">
          {details.map((d, i) => (
            <div key={`${d.label}-${i}`} className="flex justify-between gap-3">
              <dt className="text-gray-500 dark:text-slate-400">{d.label}</dt>
              <dd className="text-right font-medium text-gray-800 dark:text-slate-200">{d.value ?? '—'}</dd>
            </div>
          ))}
        </dl>
      )}

      {Array.isArray(node.items) && (
        <div className="mt-4">
          <div className="mb-1.5 text-xs font-semibold uppercase text-gray-500 dark:text-slate-400">Items</div>
          {!items.length && <div className="text-sm text-gray-500 dark:text-slate-400">Nothing here yet.</div>}
          <ul className="divide-y divide-gray-100 dark:divide-slate-800">
            {items.map((it, i) => (
              <li key={i} className="py-1.5 text-sm">
                {it.opportunity_id && onOpenOpportunity
                  ? <button type="button" onClick={() => onOpenOpportunity(it.opportunity_id)} className="text-left text-blue-600 hover:underline dark:text-blue-400">{it.text}</button>
                  : <div className="text-gray-800 dark:text-slate-200">{it.text}</div>}
                {it.sub && <div className="text-xs text-gray-500 dark:text-slate-400">{it.sub}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {wantRuns && (
        <div className="mt-4">
          <div className="mb-1.5 text-xs font-semibold uppercase text-gray-500 dark:text-slate-400">Recent runs</div>
          {error && <div className="mb-2"><Notice tone="error">{error}</Notice></div>}
          {runs === null && !error && <div className="text-sm text-gray-500 dark:text-slate-400">Loading...</div>}
          {runs && !runs.length && <div className="text-sm text-gray-500 dark:text-slate-400">No runs yet.</div>}
          {runs && runs.length > 0 && (
            <ul className="space-y-2">
              {runs.map((r, i) => <RunCard key={r.id || i} run={r} onOpenOpportunity={onOpenOpportunity} />)}
            </ul>
          )}
        </div>
      )}
    </aside>
  );
};

/* ---- canvas ---- */

export default function Network({ teamId, onOpenOpportunity, onApproach }) {
  const [net, setNet] = useState(null);
  const [error, setError] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);
  const [selected, setSelected] = useState(null);
  const [, setClock] = useState(0);

  // Poll every 3 s while the tab is visible, every 15 s otherwise. A failed poll keeps the last good payload.
  useEffect(() => {
    setNet(null);
    setError(null);
    setFetchedAt(null);
    setSelected(null);
    if (!teamId) return undefined;
    let alive = true;
    let busy = false;
    let timer = null;
    const poll = async () => {
      clearTimeout(timer);
      busy = true;
      try {
        const data = await api(`/teams/${teamId}/network`);
        if (alive) {
          setNet(data.network || null);
          setFetchedAt(Date.now());
          setError(null);
        }
      } catch (err) {
        if (alive) setError(err.message);
      }
      busy = false;
      if (alive) timer = setTimeout(poll, document.visibilityState === 'visible' ? 3000 : 15000);
    };
    const onVisible = () => { if (document.visibilityState === 'visible' && !busy) poll(); };
    poll();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      alive = false;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [teamId]);

  // Ticks the "updated Xs ago" line and running durations.
  useEffect(() => {
    const t = setInterval(() => setClock((c) => c + 1), 1000);
    return () => clearInterval(t);
  }, []);

  const nodes = useMemo(() => list(net?.nodes).filter((n) => n && n.id), [net]);
  const L = useMemo(() => layout(nodes), [nodes]);
  const drawn = useMemo(() => {
    let width = W;
    const edges = [];
    list(net?.edges).forEach((e, i) => {
      if (!e || e.from === e.to) return;
      const r = route(e.from, e.to, L);
      if (!r) return;
      width = Math.max(width, Math.ceil(r.reach) + 8);
      edges.push({ key: `${e.from}>${e.to}>${i}`, d: r.d, look: EDGE[e.state] || EDGE.idle });
    });
    edges.sort((a, b) => a.look.rank - b.look.rank);
    return { edges, width };
  }, [net, L]);

  const sel = nodes.find((n) => n.id === selected) || null;
  const worker = net?.worker || {};
  const team = net?.team || {};

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${error ? 'bg-red-500' : fetchedAt ? 'animate-pulse bg-green-500' : 'bg-gray-300 dark:bg-slate-600'}`} />
          <span className="font-medium text-gray-700 dark:text-slate-300">Live</span>
          <span>{fetchedAt ? `updated ${timeAgo(fetchedAt)}` : 'connecting...'}</span>
        </span>
        {net && <span>{worker.on ? `Worker on, last tick ${timeAgo(worker.last_tick_at)}` : 'Worker is off'}</span>}
        {team.status === 'paused' && <span className="font-medium text-amber-600 dark:text-amber-400">Team paused</span>}
      </div>
      {error && <Notice tone="error">Could not refresh the network: {error}{net ? ' Showing the last data received.' : ''}</Notice>}

      <div className="flex flex-col gap-4 xl:flex-row">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="overflow-x-auto rounded-xl border border-gray-200 bg-gray-50 [--dot:#dadce0] dark:border-slate-800 dark:bg-slate-950 dark:[--dot:#334155]" style={GRID}>
            {!net || !nodes.length ? (
              <div className="p-10 text-center text-sm text-gray-500 dark:text-slate-400">
                {net ? 'No agents to show yet.' : error ? 'The network could not be loaded.' : 'Loading...'}
              </div>
            ) : (
              <div className="relative mx-auto" style={{ width: drawn.width, height: L.height }}>
                <svg width={drawn.width} height={L.height} className="absolute inset-0" aria-hidden="true">
                  {drawn.edges.map((e) => (
                    <path key={e.key} d={e.d} fill="none" className={`transition-colors ${e.look.cls}`} strokeWidth={e.look.w} strokeDasharray={e.look.dash} />
                  ))}
                </svg>
                {list(net.columns).filter((c) => COLS[c.key]).map((c) => (
                  <div key={c.key} className="absolute whitespace-nowrap text-xs leading-4 text-gray-500 dark:text-slate-400" style={{ left: COLS[c.key].x, top: 12 }}>{c.label}</div>
                ))}
                {nodes.map((n) => L.box[n.id] && (
                  <NodeCard key={n.id} node={n} box={L.box[n.id]} selected={n.id === selected} onSelect={() => setSelected((s) => (s === n.id ? null : n.id))} />
                ))}
              </div>
            )}
          </div>
          <Legend />
        </div>
        {sel && (
          <Panel key={sel.id} teamId={teamId} node={sel} onClose={() => setSelected(null)} onOpenOpportunity={onOpenOpportunity} onApproach={onApproach} />
        )}
      </div>
    </div>
  );
}
